import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://ssfdetail.com';
const DEFAULT_OG_IMAGE = 'https://customer-assets.emergentagent.com/job_carcare-elite/artifacts/vb3klkvj_Award%20Winning%20PPF%20And%20Ceramic%20Coating%20In%20Boca%20Raton%2C%20FL%20%20SSF%20Detail.png';

/**
 * SEO component — injects per-page meta, Open Graph, Twitter, and structured data.
 *
 * Props:
 * - title:        Page title (required)
 * - description:  Meta description (required)
 * - canonical:    Relative path, e.g. "/ceramic-coating" (required)
 * - keywords:     Comma-separated keywords string (optional)
 * - ogImage:      Full URL to social share image (optional)
 * - schema:       Single JSON-LD object OR array of objects (optional)
 * - robots:       Robots directive (optional; defaults to indexable). Set to
 *                 "noindex, follow" on 404s, thank-you pages, admin, etc.
 */
const SEO = ({ title, description, canonical, keywords, ogImage, schema, robots }) => {
  const fullUrl = `${SITE_URL}${canonical || ''}`;
  const image = ogImage || DEFAULT_OG_IMAGE;
  const schemaArray = Array.isArray(schema) ? schema : schema ? [schema] : [];
  const robotsValue = robots || 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robotsValue} />
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="SSF Detail" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Per-page JSON-LD structured data */}
      {schemaArray.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
};

/* ---------- Schema helpers ---------- */

export const buildBreadcrumb = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`,
  })),
});

export const buildService = ({
  name,
  description,
  path,
  image,
  serviceType,
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name,
  description,
  serviceType: serviceType || name,
  url: `${SITE_URL}${path}`,
  ...(image && { image }),
  provider: {
    '@type': 'LocalBusiness',
    '@id': 'https://ssfdetail.com/#business',
    name: 'SSF Detail',
    telephone: '+1-561-489-7527',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '850 NW 1st Ave',
      addressLocality: 'Boca Raton',
      addressRegion: 'FL',
      postalCode: '33432',
      addressCountry: 'US',
    },
  },
  areaServed: [
    { '@type': 'City', name: 'Boca Raton' },
    { '@type': 'City', name: 'Delray Beach' },
    { '@type': 'City', name: 'Fort Lauderdale' },
    { '@type': 'City', name: 'West Palm Beach' },
    { '@type': 'City', name: 'Miami' },
    { '@type': 'City', name: 'Tampa' },
  ],
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '5.0',
    reviewCount: '110',
    bestRating: '5',
    worstRating: '1',
  },
});

export const buildLocationService = ({ city, path }) => ({
  '@context': 'https://schema.org',
  '@type': 'AutoDetailing',
  name: `SSF Detail — Auto Detailing in ${city}, FL`,
  description: `Premium mobile and shop-based car detailing, ceramic coating, paint protection film, and window tinting serving ${city}, Florida.`,
  url: `${SITE_URL}${path}`,
  telephone: '+1-561-489-7527',
  image: DEFAULT_OG_IMAGE,
  address: {
    '@type': 'PostalAddress',
    streetAddress: '850 NW 1st Ave',
    addressLocality: 'Boca Raton',
    addressRegion: 'FL',
    postalCode: '33432',
    addressCountry: 'US',
  },
  areaServed: {
    '@type': 'City',
    name: city,
    containedInPlace: {
      '@type': 'AdministrativeArea',
      name: 'Florida',
    },
  },
  priceRange: '$$$',
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '5.0',
    reviewCount: '110',
    bestRating: '5',
    worstRating: '1',
  },
});

export default SEO;
